import * as rd from '@devexperts/remote-data-ts'
import { cn } from '@spiaggeit/spit-ui'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import {
  Outlet,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { Header } from '@/components/Header'
import { useAppPath } from '@/hooks/useAppPath.ts'
import { useResetInsertInsuranceProducts } from '@/hooks/useResetInsertInsurance'
import { useSetupKiosk } from '@/hooks/useSetupKiosk'
import { useSetupWhiteLabel } from '@/hooks/useSetupWhiteLabel.ts'
import { appSlice } from '@/store/appSlice'
import { bookingAvailabilitySlice } from '@/store/bookingAvailabilitySlice'
import { insertPeriodSlice } from '@/store/insertPeriodSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { servicesSlice } from '@/store/servicesSlice'
import { selectIsWhiteLabelActive, widgetSlice } from '@/store/widgetSlice.ts'

import { getNextPathAfterChoosingPeriod } from '@/utils/getNextPathAfterChoosingPeriod'
import {
  CHOOSE_PRODUCT_PATH,
  INSERT_PERIOD_PATH,
  LANDING_KIOSK_PATH,
  MAP_PATH,
  ROOT_PATH,
} from '../app/router/paths'
import { DataLayer } from '../components/DataLayer'
import { Footer } from '../components/Footer'
import { Toast } from '../components/Toast'
import { useAppDispatch, useAppSelector } from '../hooks/store'
import { useApp } from '../hooks/useApp'
import { useCurrentPath } from '../hooks/useCurrentPath'
import { cartSlice } from '../store/cartSlice'
import { loadingSlice } from '../store/loadingSlice'

export function Root() {
  useApp()

  const isKiosk = useAppSelector(appSlice.selectors.isKiosk)
  const license = useAppSelector(licenseSlice.selectors.licenseRemoteData)
  const licenseSelector = useAppSelector(licenseSlice.selectors.license)
  const isRootPath = useMatch(ROOT_PATH)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentPath } = useCurrentPath()
  const isCartPaid = useAppSelector(cartSlice.selectors.isCartPaid)
  const insertPeriodPath = useAppPath(INSERT_PERIOD_PATH)
  const landingKioskPath = useAppPath(LANDING_KIOSK_PATH)
  const isWhiteLabelActive = useAppSelector(selectIsWhiteLabelActive)
  const bookingAvailability = useAppSelector(
    bookingAvailabilitySlice.selectors.self
  )
  const bookingPeriod = useAppSelector(
    insertPeriodSlice.selectors.bookingPeriod
  )
  const groupIds = useAppSelector(servicesSlice.selectors.groupIds)
  const bookingAvailabilty = useAppSelector(
    bookingAvailabilitySlice.selectors.self
  )
  const isLoadingStackEmpty = useAppSelector(
    loadingSlice.selectors.isLoadingStackEmpty
  )
  const pageBackgroundImage = useAppSelector(
    widgetSlice.selectors.backgroundImage
  )
  const textFont = useAppSelector(widgetSlice.selectors.textFont)
  const { resetInsertInsuranceProducts } = useResetInsertInsuranceProducts()

  const isMapPath = currentPath === MAP_PATH

  const defaultInitialPath = isKiosk ? landingKioskPath : insertPeriodPath

  useEffect(() => {
    if (rd.isSuccess(license)) {
      dispatch(licenseSlice.actions.fetchLicenseStrings())
    }
  }, [license])

  useEffect(() => {
    if (!isRootPath) return

    if (rd.isSuccess(bookingAvailability) && licenseSelector) {
      // this is useful if someone lands on the root page
      // and there's a start and end period set via query paramters
      const nextPath = getNextPathAfterChoosingPeriod({
        bookingAvailability: bookingAvailabilty,
        bookingPeriod,
        groupIds,
        license: licenseSelector,
      })
      if (nextPath) {
        navigate(nextPath, { replace: true })
      } else {
        navigate(`${defaultInitialPath}?${searchParams}`, { replace: true })
      }
    } else if (rd.isFailure(bookingAvailability)) {
      navigate(defaultInitialPath)
    }
  }, [
    isRootPath,
    license,
    licenseSelector,
    bookingAvailability,
    bookingPeriod,
    groupIds,
    defaultInitialPath,
  ])

  useEffect(() => {
    if ([INSERT_PERIOD_PATH, CHOOSE_PRODUCT_PATH].includes(currentPath)) {
      resetInsertInsuranceProducts()
      dispatch(cartSlice.actions.resetCartSlice())
    }
  }, [currentPath, isCartPaid])

  useSetupWhiteLabel()
  useSetupKiosk()

  const needsGrayBackground = ![
    LANDING_KIOSK_PATH,
    CHOOSE_PRODUCT_PATH,
    INSERT_PERIOD_PATH,
    MAP_PATH,
  ].includes(currentPath)

  const isBackgroundImageApplied =
    isWhiteLabelActive && !!pageBackgroundImage && !isMapPath

  return (
    <>
      <Helmet>
        {textFont ? (
          <link
            href={`https://fonts.googleapis.com/css2?family=${textFont}:wght@400;500;700&display=swap`}
            rel="stylesheet"
          />
        ) : null}
      </Helmet>

      <div
        className={cn('flex min-h-full flex-col', {
          'bg-cover bg-center': isBackgroundImageApplied,
          'bg-gray-25': !isWhiteLabelActive && needsGrayBackground,
          'page-background-color': isWhiteLabelActive,
        })}
        {...(isBackgroundImageApplied && {
          style: {
            backgroundImage: `linear-gradient(to right, rgba(0,0,0, 0.2), rgba(0,0,0,0.2)), url(${pageBackgroundImage})`,
          } as React.CSSProperties,
        })}
      >
        <div
          className={cn('flex grow flex-col transition-opacity duration-300', {
            'opacity-0': !isLoadingStackEmpty,
          })}
        >
          <Header />
          <Outlet />
          <Footer />
        </div>

        <Toast />
        <DataLayer />
      </div>
    </>
  )
}
